import React, { useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import uniq from 'lodash.uniq';
import PMMeasures from './PMMeasures';
import { studentType } from '../../../constants/prop-types';
import {
    MEASURE_NAMES,
    MEASURE_SCORE_DISPLAY_NAMES,
    SKILL_DISPLAY_NAMES,
    RECOMMENDATION_STATUSES_ICONS,
} from '../../../constants/assessments';
import { GRADE } from '../../../constants/enrollment';
import { DIBELS8, IDEL2 } from '../../../constants/applications';
import { FORMS_UID, F1_KIT_FORM_LABEL } from '../../../constants/forms';
import { useAppContext } from '../../../common/contexts/AppContext';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import AssessLink from '../assess-link/AssessLink';
import PMBanner from '../../banner/PMBanner';
import PMRecommendationNotification from '../../banner/PMRecommendationNotification';
import SelectableGradeForm from './SelectableGradeForm';
import { DICTIONARY_KEYS, localize } from '../../../common/localization';
import { getMeasureScoreBySkillName } from '../../../common/utils/measureScoreBySkillName';
import { isDigitalFormsPopupEnabled } from '../../../common/popups/utils';
import { getFormParamsByFormUid } from './utils';
import { constructStartPMAssessmentLink } from '../../../common/navigation/navigation';
import { useLanguageContext } from '../../../common/contexts/LanguageContext';
import { useBannerContext } from '../../../common/contexts/BannerContext';
import InfoIcon from '../info-icon/InfoIcon';
import PMMeasuresBannersContainer from '../../banner/PMMeasuresBannerContainer';
import { ENGLISH } from '../../../constants/languages';

const getAllRecommendationStatuses = skillsStatusesByApplication =>
    Object.keys(skillsStatusesByApplication).reduce((appSkillsStatuses, app) => {
        const statuses = skillsStatusesByApplication[app]
            .map(skillsStatus => skillsStatus.recommendationStatus)
            .filter(recommendationStatus => recommendationStatus);
        return { ...appSkillsStatuses, [app]: [...uniq(statuses)] };
    }, {});

const getRecommendationStatusIcon = recommendationStatus => {
    const statusIconCssClass = classNames(
        'dashboard-icon-amplify',
        'dashboard-' + RECOMMENDATION_STATUSES_ICONS[recommendationStatus],
    );
    return (
        <span className='dashboard-measure-status'>
            <i className={statusIconCssClass} />
        </span>
    );
};

const getRecommendedSkillsRows = (
    skillsStatusesByApplication,
    formsMap,
    student,
    nextFormUids,
    { isDigitalPopupEnabled },
    { grade, period },
    showDigitalPopup,
    showPMFormSelectionPopup,
    showOrfG5PMForm1AltTextPopup,
    classId,
    openPMCPBanner,
) =>
    Object.keys(skillsStatusesByApplication)
        .sort() // IDEL2 or D8 measures should be before TRC or TRC Spanish
        .reduce((recommendedSkillRows, application) => {
            const recommendedAppSkills = skillsStatusesByApplication[application].filter(
                skill => skill.isRecommended || skill.hasPausedResult, // TODO: move paused result to recommendation logic
            );
            const appSkillsRows = recommendedAppSkills.map(skillStatus => {
                const {
                    skillName,
                    measureName,
                    formUid,
                    hasPausedResult,
                    accessLink,
                    recommendationStatus,
                } = skillStatus;

                const forms = get(formsMap, formUid, {});
                const { sequence } = forms;
                const { F1_PDF, F1_KIT } = FORMS_UID;

                const assessLinkType = hasPausedResult
                    ? AssessLink.MODE.RESUME
                    : AssessLink.MODE.REQUIRED;

                const digitalPopupHandler = () => {
                    showDigitalPopup({
                        assessmentLink: accessLink,
                        grade,
                        studentId: student.id,
                        skillName,
                        period,
                        measureName,
                        showPMFormSelectionPopup,
                    });
                };

                const orfF1PDFAssessLink = constructStartPMAssessmentLink(
                    DIBELS8,
                    classId,
                    student.id,
                    getFormParamsByFormUid(F1_PDF, formsMap),
                );

                const orfF1KitAssessLink = constructStartPMAssessmentLink(
                    DIBELS8,
                    classId,
                    student.id,
                    getFormParamsByFormUid(F1_KIT, formsMap),
                );

                const orfG5PMForm1AltTextPopupHandler = () => {
                    showOrfG5PMForm1AltTextPopup({ orfF1PDFAssessLink, orfF1KitAssessLink });
                };

                const isG5OrfF1KitFormRecommended =
                    forms.grade === GRADE.FIVE &&
                    forms.measure === MEASURE_NAMES.ORF &&
                    forms.label.includes(F1_KIT_FORM_LABEL);

                const assessLink = () => {
                    if (isDigitalFormsPopupEnabled(sequence, measureName, isDigitalPopupEnabled)) {
                        return (
                            <AssessLink
                                mode={assessLinkType}
                                onClickHandler={digitalPopupHandler}
                            />
                        );
                    }

                    if (isG5OrfF1KitFormRecommended) {
                        return (
                            <AssessLink
                                mode={assessLinkType}
                                onClickHandler={orfG5PMForm1AltTextPopupHandler}
                            />
                        );
                    }
                    return <AssessLink mode={assessLinkType} path={accessLink} />;
                };

                const measureScoreName = getMeasureScoreBySkillName(skillName, measureName);

                return (
                    <div className='dashboard-measure-info' key={skillName}>
                        <div className='dashboard-skill'>
                            <span className='dashboard-skill-name'>
                                {localize(SKILL_DISPLAY_NAMES[skillName])}
                            </span>
                            <span className='dashboard-measure-name'>
                                {localize(MEASURE_SCORE_DISPLAY_NAMES[measureScoreName])}
                            </span>
                            {measureName === MEASURE_NAMES.CP && (
                                <InfoIcon onClickHandler={openPMCPBanner} />
                            )}
                            {recommendationStatus &&
                                getRecommendationStatusIcon(recommendationStatus)}
                        </div>
                        {measureName === MEASURE_NAMES.CP ? (
                            <div className='dashboard-centered' data-testid="CP-dash">
                                —
                            </div>
                        ) : (
                            <SelectableGradeForm
                                studentId={student.id}
                                form={get(formsMap, formUid, {})}
                                nextFormUidsByMeasures={nextFormUids?.[application]}
                                skillName={skillName}
                                onClickHandler={digitalPopupHandler}
                            />
                        )}
                        {measureName === MEASURE_NAMES.CP ? (
                            <div className='dashboard-centered'>—</div>
                        ) : (
                            assessLink()
                        )}
                    </div>
                );
            });
            return [...recommendedSkillRows, ...appSkillsRows];
        }, []);

const getNoRecommendationMessage = hasInterventionInstruction =>
    hasInterventionInstruction ? (
        <div className='dashboard-message'>All the required measures have been completed.</div>
    ) : (
        <div className='dashboard-message'>
            {localize(DICTIONARY_KEYS.YOU_ARE_DONE_PROGRESS_MONITORING_THIS_STUDENT_TODAY)}.
        </div>
    );

const getPMMeasureNotifications = recommendationStatuses =>
    Object.keys(recommendationStatuses).map(app =>
        recommendationStatuses[app].map(status => (
            <PMRecommendationNotification
                key={status}
                recommendationStatus={status}
                appName={app}
            />
        )),
    );

const PMRecommendation = ({
    student,
    skillsStatusesByApplication,
    hasInterventionInstruction,
    nextFormUids,
    classId,
}) => {
    const { formsMap } = useAppContext();
    const lang = useLanguageContext();
    const { id: studentUid } = student;

    const selectedApp = lang === ENGLISH ? DIBELS8 : IDEL2;

    const {
        userPopupsSettings,
        params,
        actions: { showDigitalPopup, showPMFormSelectionPopup, showOrfG5PMForm1AltTextPopup },
    } = usePopupContext();

    const {
        actions: { enablePMCPBanner, showPMCPBanner },
    } = useBannerContext();

    const openPMCPBanner = () => showPMCPBanner({ classId, studentUid });

    const recommendationStatuses = getAllRecommendationStatuses(skillsStatusesByApplication);

    const recommendedSkillsRows = getRecommendedSkillsRows(
        skillsStatusesByApplication,
        formsMap,
        student,
        nextFormUids,
        userPopupsSettings,
        params,
        showDigitalPopup,
        showPMFormSelectionPopup,
        showOrfG5PMForm1AltTextPopup,
        classId,
        openPMCPBanner,
    );

    const isAvailable =
        IDEL2 in skillsStatusesByApplication &&
        skillsStatusesByApplication?.[IDEL2].find(
            el => el.measureName === MEASURE_NAMES.CP && el.isRecommended,
        );

    useEffect(() => {
        enablePMCPBanner({
            classId,
            studentUid,
            isAvailable: isAvailable?.isRecommended ?? false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedApp, isAvailable, classId, studentUid]);

    return (
        <>
            {recommendedSkillsRows.length > 0 ? (
                <>
                    {recommendationStatuses[selectedApp]?.length === 0 && (
                        <PMBanner
                            hasInterventionInstruction={hasInterventionInstruction}
                            studentUid={studentUid}
                        />
                    )}
                    <PMMeasuresBannersContainer studentUid={studentUid} classId={classId} />
                    {getPMMeasureNotifications(recommendationStatuses)}
                    <div className='dashboard-measure-scores dashboard-recommended'>{recommendedSkillsRows}</div>
                </>
            ) : (
                getNoRecommendationMessage(hasInterventionInstruction)
            )}
            <PMMeasures
                student={student}
                skillsStatusesByApplication={skillsStatusesByApplication}
                nextFormUids={nextFormUids}
                hasInterventionInstruction={hasInterventionInstruction}
            />
        </>
    );
};

PMRecommendation.propTypes = {
    student: studentType.isRequired,
    skillsStatusesByApplication: PropTypes.shape({
        dibels8: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
        r3d: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
        r3dspanish: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
        burst: PropTypes.arrayOf(
            PropTypes.shape({
                skillName: PropTypes.string,
                accessLink: PropTypes.string,
                hasPausedResult: PropTypes.bool,
                isRecommended: PropTypes.bool,
            }),
        ),
    }).isRequired,
    hasInterventionInstruction: PropTypes.bool,
    nextFormUids: PropTypes.object.isRequired,
    classId: PropTypes.string.isRequired,
};

PMRecommendation.defaultProps = {
    hasInterventionInstruction: false,
};

export default PMRecommendation;
